// =====================================================================
//  CLIENTE DE SUPABASE
// =====================================================================

import { createClient } from '@supabase/supabase-js'
import type { Database } from '../tipos/supabase'
import { SinPermiso } from './errores'

const url = import.meta.env.VITE_SUPABASE_URL
const clave = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !clave) {
  throw new Error(
    'Faltan VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY. ' +
      'Copia .env.example a .env y complétalos con los valores del proyecto.',
  )
}

/**
 * El cliente va tipado con el esquema real (`npm run tipos` regenera
 * src/tipos/supabase.ts desde la base de datos enlazada). Con eso, un
 * nombre de tabla, de vista o de columna mal escrito falla al compilar en
 * vez de devolver un error en tiempo de ejecución.
 *
 * Nota sobre las columnas `numeric`: los tipos generados las declaran
 * `number`, y así llegan por PostgREST. Los tipos del dominio usan
 * `Cifra = number | string | null` porque las pruebas leen el mismo
 * esquema por el protocolo nativo de Postgres, que las entrega como
 * string para no perder precisión. `num()` normaliza las dos formas.
 */
export const supabase = createClient<Database>(url, clave, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // La aplicación se sirve estática desde remates2026.consa.cl, con
    // ruteo por hash: no hay servidor que atienda /auth/callback.
    flowType: 'pkce',
  },
})

/**
 * Ejecuta una escritura y garantiza que de verdad ocurrió.
 *
 * Usar siempre con `.select()` al final de la cadena, para que PostgREST
 * devuelva las filas afectadas y se puedan contar.
 */
export async function mutar<T>(
  consulta: PromiseLike<{ data: T[] | null; error: { message: string; code?: string } | null }>,
): Promise<T[]> {
  const { data, error } = await consulta
  if (error) throw error
  if (!data || data.length === 0) throw new SinPermiso()
  return data
}
